import { BrowserSupport } from '@tableau/browser-support';
import { getAppStore } from '../../app/AppStore';
import { NavPanelLogoProps } from '../../app/layout/NavPanelLogo';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { Messages } from '../../codegen/Localize';
import { ByteSizeConversionFactor } from '../../commonInfra/utils/SizeFilter';
import { ISettingsCoordinatorCtrlScope, SettingsFormService } from '../../settings/directives/tbSettingsCoordinator';
import { ILogo, LogoService, LogoType } from '../../settings/siteLogo/services/LogoService';
import { CommonErrorCode, OnlineErrorCode } from '../helpers/ErrorCodes';
import { LogoHelper } from '../../settings/siteLogo/helpers/LogoHelper';
import { Module } from '../Module';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';

// Delete when removing settingsReactMigration2 feature flag
export interface IUpdateSiteLogoFormScope extends ISettingsCoordinatorCtrlScope {
  // Form state
  updateSiteCustomLogo: ng.IFormController;
  resetToDefault: boolean; // True if user has selected to reset to the default logo
  logoFile: File; // File API is not supported in IE 8-9
  logoFilename: string; // Needed for preview-less fall back
  compactLogoFile: File;
  compactLogoFilename: string;
  errorMessage: string;
  formIsValid: boolean;
  formIsDirty: boolean;
  dragAndDropEnabled: boolean;
  previewEnabled: boolean;
  logoPreviewProps: NavPanelLogoProps;
  compactLogoPreviewProps: NavPanelLogoProps;
  separateCompactLogo: boolean;

  // Elements
  logoFileInput: JQuery;
  compactLogoFileInput: JQuery;
  dropArea: JQuery;
  logoWrapper: JQuery;
  compactWrapper: JQuery;

  init: () => ng.IPromise<any>;
  handleFormSubmitResult: (results: IResult[]) => void;
  submitSiteLogoForm: () => ng.IPromise<any>;
  usingCustomLogo: () => boolean; // Returns true if we are currently using custom logo
  handleResetToDefaultClick: () => void;
  onFileAddedOrChanged: (filename: string, file: File, logoType: LogoType) => void;
  onFileDrop: (file: File, logoType: LogoType) => void;
  onLogoFileInputChange: () => void;
  onCompactLogoFileInputChange: () => void;
  toggleSeparateCompactLogo: () => void;
}

Module.instance().directive('tbUpdateSiteLogoForm', <any[]>[
  () => {
    return {
      templateUrl: 'updateSiteLogoForm.html',
      replace: true,
      restrict: 'E',
      scope: { logoType: '@' },
      require: '^?tbSettingsCoordinator',
      controller: UpdateSiteLogoCtrl,
      link: ($scope: IUpdateSiteLogoFormScope, form: JQuery) => {
        $scope.logoFileInput = form.find('#updateSiteCustomLogoInput');
        $scope.compactLogoFileInput = form.find('#updateSiteCustomCompactLogoInput');

        const wrapperClass = '.tb-update-site-logo-preview-img-wrapper';
        $scope.logoWrapper = form.find(`.tb-update-site-logo-preview-pseudo-header-left-nav ${wrapperClass}`);
        $scope.compactWrapper = form.find(`.tb-update-site-logo-preview-pseudo-compact-left-nav ${wrapperClass}`);

        $scope.logoFileInput.on('change', () => $scope.$evalAsync($scope.onLogoFileInputChange));
        $scope.compactLogoFileInput.on('change', () => $scope.$evalAsync($scope.onCompactLogoFileInputChange));

        $scope.localForm.formController = $scope.updateSiteCustomLogo;
        $scope.sharedForm.registerFormStateByName($scope.localForm, 'updateSiteCustomLogo');

        $scope.init();
      }
    };
  }
]);


export class UpdateSiteLogoCtrl {

  private static SupportedImageTypes = ['image/png', 'image/jpeg', 'image/gif'];
  private static MaxImageSize = ByteSizeConversionFactor.ONE_MB * 2;

  public static $inject = [
    '$scope',
    '$q',
    '$timeout',
    'SettingsFormService'
  ];
  $onInit: () => void;

  constructor(
      private $scope: IUpdateSiteLogoFormScope,
      $q: ng.IQService,
      private $timeout: ng.ITimeoutService,
      formService: SettingsFormService
  ) {
    this.$onInit = () => {
      $scope.usingCustomLogo = () => (LogoService.isUsingCustomSiteLogo() || LogoService.hasCustomCompactLogo());
      $scope.dragAndDropEnabled = BrowserSupport.SupportsDragAndDropEvents() &&
        BrowserSupport.SupportsFormData() &&
        !BrowserSupport.IsTouch();
      $scope.previewEnabled = BrowserSupport.SupportsFileReader();

      $scope.init = (): ng.IPromise<any> => {
        $scope.logoFileInput.val(null);
        $scope.compactLogoFileInput.val(null);
        $scope.formIsDirty = false;
        $scope.resetToDefault = false;
        $scope.formIsValid = true;
        $scope.errorMessage = undefined;
        $scope.logoFile = undefined;
        $scope.logoFilename = undefined;
        $scope.compactLogoFile = undefined;
        $scope.compactLogoFilename = undefined;
        $scope.separateCompactLogo = LogoService.hasCustomCompactLogo();
        this.updateLogoPreview(LogoService.getNavPanelLogo());
        this.updateCompactLogoPreview(LogoService.getCompactLogo());
        return $q.when({});
      };

      // Register form for syncing with form Save/Revert controls
      $scope.sharedForm = formService;
      $scope.localForm = {
        formController: $scope.updateSiteCustomLogo,
        save: () => $scope.submitSiteLogoForm(),
        cancel: () => $scope.init(),
        isBusy: () => false,
        isFormUnchanged: () => !$scope.formIsDirty,
        isValid: () => $scope.formIsValid
      };

      $scope.onFileAddedOrChanged = async (filename: string, file: File, logoType: LogoType) => {
        $scope.formIsDirty = true;

        // If the user had clicked "reset to default" but then tries uploading a new logo, reset the state of the form to
        // the logos that are currently saved before applying the new logo.
        if ($scope.resetToDefault) {
          if (logoType === 'compact') {
            this.updateLogoPreview(LogoService.getNavPanelLogo());
          }
          $scope.resetToDefault = false;
        }

        if (file && !UpdateSiteLogoCtrl.SupportedImageTypes.includes(file.type)) {
          $scope.formIsValid = false;
          $scope.errorMessage = Messages.uploadSiteLogoErrorFileTypeNotSupported();
        } else if (file && file.size && file.size > UpdateSiteLogoCtrl.MaxImageSize) {
          $scope.formIsValid = false;
          $scope.errorMessage = Messages.uploadSiteLogoErrorFileLargerThan2Megabytes();
        } else {
          $scope.formIsValid = true;
          $scope.errorMessage = undefined;

          if (logoType === 'compact') {
            $scope.compactLogoFile = file;
            $scope.compactLogoFilename = filename;
          } else {
            $scope.logoFile = file;
            $scope.logoFilename = filename;
          }

          if ($scope.previewEnabled) {
            $scope.logoWrapper.addClass('tb-without-img');
            $scope.compactWrapper.addClass('tb-without-img');

            if (logoType === 'compact') {
              await this.updateCompactLogo(file, filename);
            } else {
              await this.updateLogo(file, filename);
            }

            $timeout(() => $scope.logoWrapper.removeClass('tb-without-img'), 50);
            $timeout(() => $scope.compactWrapper.removeClass('tb-without-img'), 50);
          }
        }
      };

      $scope.onFileDrop = async (file: File, logoType: LogoType) => {
        if ($scope.dragAndDropEnabled) {
          if (logoType === 'compact') {
            $scope.compactLogoFileInput.val(null);
            $scope.separateCompactLogo = true;
          } else {
            $scope.logoFileInput.val(null);
          }
          await $scope.onFileAddedOrChanged(file.name, file, logoType);
        }
      };

      $scope.onLogoFileInputChange = async () => await this.fileInputChanged('header');
      $scope.onCompactLogoFileInputChange = async () => await this.fileInputChanged('compact');

      $scope.handleResetToDefaultClick = (): void => {
        $scope.logoFileInput.val(null);
        $scope.compactLogoFileInput.val(null);
        $scope.formIsDirty = true;
        $scope.resetToDefault = true;
        $scope.formIsValid = true;
        $scope.errorMessage = undefined;
        $scope.logoFile = undefined;
        $scope.logoFilename = undefined;
        $scope.compactLogoFile = undefined;
        $scope.compactLogoFilename = undefined;
        $scope.separateCompactLogo = false;
        this.updateLogoPreview(LogoService.getDefaultHeaderLogo());
        this.updateCompactLogoPreview(LogoService.getDefaultCompactLogo());
      };

      $scope.submitSiteLogoForm = (): ng.IPromise<any> => {
        if ($scope.formIsDirty) {
          let updatePromise: ng.IPromise<IResult[]>;
          if (!$scope.resetToDefault) {
            // Only update the logos that have changed
            const shouldUpdateCompact = !!$scope.compactLogoFile || (LogoService.hasCustomCompactLogo() && !$scope.separateCompactLogo);
            updatePromise = $q.all([
              $scope.logoFile && LogoService.updateSiteLogo($scope.logoFile, 'header'),
              shouldUpdateCompact && LogoService.updateSiteLogo($scope.compactLogoFile, 'compact')
            ]);
          } else {
            updatePromise = $q.all([
              LogoService.updateSiteLogo(null, 'header'),
              LogoService.updateSiteLogo(null, 'compact')
            ]);
          }
          return updatePromise.then((results: IResult[]) => $scope.handleFormSubmitResult(results));
        }
      };

      $scope.handleFormSubmitResult = (results: IResult[]): ng.IPromise<any> => {
        for (const result of results) {
          if (result && result.errors && result.errors.length) {
            switch (result.errors[0].code) {
              case CommonErrorCode.FileSizeTooLarge:
                $scope.errorMessage = Messages.uploadSiteLogoErrorFileLargerThan2Megabytes();
                break;
              case OnlineErrorCode.SiteCustomizationUnavailable:
                $scope.errorMessage = Messages.actionNotAvailable();
                break;
              case OnlineErrorCode.InvalidImage:
                $scope.errorMessage = Messages.uploadSiteLogoErrorImageExceedsMaxSize();
                break;
              case OnlineErrorCode.ImageProcessingError:
                $scope.errorMessage = Messages.uploadSiteLogoErrorImageProcessing();
                break;
              case OnlineErrorCode.StorageException:
                $scope.errorMessage = Messages.uploadSiteLogoErrorImageStoreError();
                break;
              default:
                $scope.errorMessage = Messages.uploadSiteLogoErrorSaveError();
                break;
            }
            return $q.reject();
          }
        }

        return getSessionManagementServiceInstance().refreshSessionInfo() // Fetch the updated logo
          .then(() => $scope.init());
      };

      $scope.logoPreviewProps = {
        logo: LogoService.getNavPanelLogo(),
        compactLogo: LogoService.getCompactLogo(),
        collapsed: false,
        disableLink: true,
        siteUrls: siteUrlsSelector(getAppStore().getState()),
      };

      $scope.compactLogoPreviewProps = {
        ...$scope.logoPreviewProps,
        collapsed: true
      };

      $scope.separateCompactLogo = LogoService.hasCustomCompactLogo();

      $scope.toggleSeparateCompactLogo = () => this.toggleSeparateCompactLogo();
    };
  }

  toggleSeparateCompactLogo() {
    this.$scope.separateCompactLogo = !this.$scope.separateCompactLogo;
    if (!this.$scope.separateCompactLogo) {
      this.$scope.formIsDirty = LogoService.hasCustomCompactLogo();
      this.$scope.compactLogoFile = null;
      this.$scope.compactLogoFilename = null;
      this.$scope.compactLogoFileInput.val(null);
      if (LogoService.hasCustomHeaderLogo() || this.$scope.logoFile) {
        const scaledHeaderLogo = LogoService.getScaledLogo(this.$scope.logoPreviewProps.logo, 'compact');
        this.updateCompactLogoPreview(scaledHeaderLogo);
      } else {
        this.updateCompactLogoPreview(LogoService.getDefaultCompactLogo());
      }
    } else {
      this.$scope.formIsDirty = !!(this.$scope.logoFile || this.$scope.compactLogoFile);
      if (!this.$scope.formIsDirty || LogoService.hasCustomCompactLogo()) {
        this.updateCompactLogoPreview(LogoService.getCompactLogo());
      }
    }
  }

  async fileInputChanged(logoType: LogoType) {
    const fileInput = logoType === 'compact' ? this.$scope.compactLogoFileInput : this.$scope.logoFileInput;
    if (fileInput.val()) {
      // File object is not accessible in all browsers
      const fileInputValue = fileInput.val() as string;
      const name = fileInputValue.split('\\').pop();
      const fileInputElement = fileInput[0] as HTMLInputElement;
      const file = fileInputElement.files && fileInputElement.files[0];
      await this.$scope.onFileAddedOrChanged(name, file, logoType);
    } else if (!this.$scope.resetToDefault) {
      // User clicked 'cancel' in OS file selection
      this.$scope.init();
    }
  }

  async updateLogo(file: File, filename: string) {
    this.$scope.logoFile = file;
    this.$scope.logoFilename = filename;

    try {
      const logo = await LogoHelper.generatePreviewLogo(file, 'header');
      this.updateLogoPreview(logo);

      if (!this.$scope.separateCompactLogo ||
          (this.$scope.separateCompactLogo && !this.$scope.compactLogoFile && !LogoService.hasCustomCompactLogo())) {
        const compactLogo = await LogoHelper.generatePreviewLogo(file, 'compact');
        this.updateCompactLogoPreview(compactLogo);
      }
    } catch (err) {
      this.$scope.errorMessage = err.message;
    }
  }

  async updateCompactLogo(file: File, filename: string) {
    this.$scope.compactLogoFile = file;
    this.$scope.compactLogoFilename = filename;

    try {
      const compactLogo = await LogoHelper.generatePreviewLogo(file, 'compact');
      this.updateCompactLogoPreview(compactLogo);
    } catch (err) {
      this.$scope.errorMessage = err.message;
    }
  }

  updateLogoPreview(logo: ILogo) {
    this.$scope.logoPreviewProps = {...this.$scope.logoPreviewProps, logo };
    this.$scope.compactLogoPreviewProps = {...this.$scope.compactLogoPreviewProps, logo };
  }

  updateCompactLogoPreview(compactLogo: ILogo) {
    this.$scope.logoPreviewProps = {...this.$scope.logoPreviewProps, compactLogo};
    this.$scope.compactLogoPreviewProps = {...this.$scope.compactLogoPreviewProps, compactLogo};
  }
}

// TODO: Remove this registration; is it unnecessary.
Module.instance().controller('UpdateSiteLogoCtrl', UpdateSiteLogoCtrl);
